// Screen navigation constants
export const SCREENS = {
  WELCOME: 'WELCOME',
  CONTACT_FORM: 'CONTACT_FORM',
  ORIENTATION_SELECTION: 'ORIENTATION_SELECTION',
  CAMERA: 'CAMERA',
  STYLE_SELECTION: 'STYLE_SELECTION',
  LOADING: 'LOADING',
  RESULT: 'RESULT',
  QR_CODE: 'QR_CODE',
  IMAGE_VIEWER: 'IMAGE_VIEWER'
};

// Universal quality enhancers - Aggressively tuned for REALISM over "art"
// UPDATED: Added "film grain, raw photo, fujifilm" to force texture and remove the plastic look
export const QUALITY_KEYWORDS = 'raw photo, unedited, 8k, hyperrealistic, photorealistic, visible skin pores, skin texture, imperfections, soft film grain, depth of field, raytracing, global illumination, shot on Sony A7R IV, 85mm lens, f/1.8, cinematic lighting, volumetric fog';

// Universal negative prompt
// UPDATED: Strictly forbids the "smooth" or "3D" look
export const UNIVERSAL_NEGATIVE_PROMPT = 'cgi, 3d render, painting, illustration, drawing, cartoon, anime, doll, plastic, wax figure, photoshop, airbrushed, smooth skin, blurred details, low quality, blurry, jpeg artifacts, floating limbs, distorted face, bad anatomy, makeup, fake, glossy skin';

// Environment-specific negative additions
export const ENVIRONMENT_NEGATIVE = 'floating head, floating body, cut off at bottom, sticker effect, green screen look, tiny subject, distant subject, giant background, mismatched perspective, bad composite, hard edges';

// Transformation-specific negative additions
export const TRANSFORMATION_NEGATIVE = 'helmet, headgear, hat, cap, face mask, weird neck, different skin tone, feminine hands on male, masculine hands on female, extra fingers, deformed hands, holding object weirdly, floating object, cartoon eyes, glowing eyes, text watermark';

// Cloudflare R2 URLs for style reference images (faster than local files)
export const CLOUDFLARE_STYLE_URLS = {
  // Environment styles
  crime_scene: 'https://pub-85724da619444065a019b48984733b66.r2.dev/2025-11-22T11:57:54.132Z-1763812678386-q03nzux.jpg',
  forensic_lab: 'https://pub-85724da619444065a019b48984733b66.r2.dev/2025-11-22T11:57:58.794Z-1763812680461-1bkqmhu.jpg',
  command_center: 'https://pub-85724da619444065a019b48984733b66.r2.dev/2025-11-22T11:58:00.861Z-1763812682298-kspbk4z.jpg',
  cyber_crime_lab: 'https://pub-85724da619444065a019b48984733b66.r2.dev/2025-11-22T11:58:02.657Z-1763812684717-n0g0lfi.jpg',
  mobile_forensic_unit: 'https://pub-85724da619444065a019b48984733b66.r2.dev/2025-11-22T11:58:05.035Z-1763812686473-91s1aa8.jpg',
  ballistics_lab: 'https://pub-85724da619444065a019b48984733b66.r2.dev/2025-11-22T11:58:07.635Z-1763812689080-pjv1kd1.jpg',
  // Transformation styles (updated with logo-free versions)
  arson_investigator: 'https://pub-85724da619444065a019b48984733b66.r2.dev/-1764081808421-mj5r220.jpg',
  bomb_disposal_technician: 'https://pub-85724da619444065a019b48984733b66.r2.dev/-1764081818786-i3zbqax.jpg',
  csi_field_agent: 'https://pub-85724da619444065a019b48984733b66.r2.dev/-1764081823424-onon338.jpg',
  cyber_specialist: 'https://pub-85724da619444065a019b48984733b66.r2.dev/-1764081827627-0uqbwiq.jpg',
  drone_pilot: 'https://pub-85724da619444065a019b48984733b66.r2.dev/-1764081832965-rcxw0h5.jpg',
  forensic_scientist: 'https://pub-85724da619444065a019b48984733b66.r2.dev/-1764081838289-3j2yb8k.jpg',
  k9_handler: 'https://pub-85724da619444065a019b48984733b66.r2.dev/-1764081844261-vympfy5.jpg',
  senior_detective: 'https://pub-85724da619444065a019b48984733b66.r2.dev/-1764081854804-7nsf1ja.jpg',
  swat_officer: 'https://pub-85724da619444065a019b48984733b66.r2.dev/-1764081859848-y0m6aja.jpg',
};

// ----------------------------------------------------------------------
// MODE 1: ENVIRONMENT STYLES (Background Changes Only)
// Text-based descriptions - AI generates background from description
// Prompts are comprehensive descriptions matching the exact preview images
// ----------------------------------------------------------------------
export const ENVIRONMENT_STYLES = {
  CRIME_SCENE: {
    id: 'crime_scene',
    name: 'Crime Scene (Night)',
    description: 'Night perimeter with tape, flashing police lights, and Dubai skyline.',
    tags: ['Scene Swap', 'Dramatic'],
    referenceImage: CLOUDFLARE_STYLE_URLS.crime_scene,
    previewImage: '/Style Images/Environment/crime-scene.jpg',
    gradient: 'from-yellow-600 to-orange-600',
    generationMode: 'environment',
    prompt: `Nighttime crime scene in Dubai. Dark night sky as backdrop. Dubai skyline in the background with the Burj Khalifa tower prominently centered and fully illuminated with white lights, flanked by other illuminated skyscrapers on both sides. In the middle ground, a white and green Dubai Police luxury sports car (Aston Martin style) parked sideways on the road, with blue police emergency lights on top casting a blue glow. The car has "Dubai Police" livery in green. Yellow "POLICE LINE DO NOT CROSS" crime scene tape stretched diagonally across the foreground, two strips of tape crossing each other. Yellow plastic evidence markers with black numbers "1" and "2" placed on the dark asphalt road. The asphalt is wet and reflective, showing reflections of the police lights and city lights. Dark, cinematic, moody atmosphere with dramatic contrast between the dark surroundings and the illuminated elements.`
  },
  FORENSIC_LAB: {
    id: 'forensic_lab',
    name: 'Advanced Forensic Lab',
    description: 'Sterile white laboratory with microscopes and glass partitions.',
    tags: ['Scene Swap', 'Science'],
    referenceImage: CLOUDFLARE_STYLE_URLS.forensic_lab,
    previewImage: '/Style Images/Environment/advanced-forensic-lab.jpg',
    gradient: 'from-green-600 to-teal-600',
    generationMode: 'environment',
    prompt: `Bright, sterile white forensic laboratory interior. White ceiling with rectangular LED panel lights arranged in a grid pattern providing bright, even illumination. On the back wall, a large flat-screen monitor displaying the Dubai Police logo (green circular emblem with Arabic text "شرطة دبي") and the word "FORENSICS" in green text below. Two rows of white laboratory benches/tables with gray metal legs arranged in the room. Multiple white professional microscopes placed on the benches. Glass beakers, Erlenmeyer flasks, and laboratory bottles containing blue liquid arranged on the work surfaces. A white glass-front cabinet on the left wall containing bottles and laboratory supplies. Extremely clean, clinical, bright white environment. Cool white color temperature. Professional forensic science laboratory atmosphere.`
  },
  COMMAND_CENTER: {
    id: 'command_center',
    name: 'Command Center',
    description: 'Massive wall of screens showing maps and surveillance feeds.',
    tags: ['Scene Swap', 'Tech'],
    referenceImage: CLOUDFLARE_STYLE_URLS.command_center,
    previewImage: '/Style Images/Environment/command-center.jpg',
    gradient: 'from-blue-600 to-cyan-600',
    generationMode: 'environment',
    prompt: `Dark high-tech police command and control center. A massive video wall spanning the entire back wall, composed of multiple screens arranged in a 4x4 grid or larger. The screens display: city road maps with cyan/blue glowing lines showing highways and streets, live surveillance camera feeds showing traffic and roads, data dashboards with graphs and metrics. All screens have a predominantly cyan and blue color scheme. Above the main video wall, a smaller monitor displays the Dubai Police logo (green emblem). In the foreground, sleek dark curved control console desks with smooth edges. The floor is dark and highly reflective, showing mirror reflections of the screens above. The room is very dark with the video wall being the primary light source. Blue ambient lighting throughout. Futuristic, high-tech operations center atmosphere.`
  },
  CYBER_CRIME_LAB: {
    id: 'cyber_crime_lab',
    name: 'Cyber Crime Unit',
    description: 'Dark room with neon blue data streams and server racks.',
    tags: ['Scene Swap', 'Cyber'],
    referenceImage: CLOUDFLARE_STYLE_URLS.cyber_crime_lab,
    previewImage: '/Style Images/Environment/cyber-crime-unit.jpg',
    gradient: 'from-green-600 to-blue-600',
    generationMode: 'environment',
    prompt: `Dark server room corridor with dramatic lighting. Two rows of tall black server racks lining both sides, creating a corridor/aisle perspective leading to the back. The server racks have vertical strips of bright emerald green LED lights running along their edges, creating striking green accent lighting. At the end of the corridor, an illuminated display panel/screen showing the Dubai Police logo (green circular emblem) with Arabic text "شرطة دبي" and "CYBER CRIME UNIT" text below. Green digital matrix code pattern visible on the back display. Dark floor with some black cables visible. The overall atmosphere is very dark with high contrast from the bright green server LEDs. Cyberpunk, tech noir aesthetic. Professional law enforcement cyber operations facility.`
  },
  MOBILE_FORENSIC_UNIT: {
    id: 'mobile_forensic_unit',
    name: 'Mobile Field Lab',
    description: 'Deployed forensic vehicle unit in an outdoor setting.',
    tags: ['Scene Swap', 'Field Ops'],
    referenceImage: CLOUDFLARE_STYLE_URLS.mobile_forensic_unit,
    previewImage: '/Style Images/Environment/mobile-forensic-unit.jpg',
    gradient: 'from-emerald-500 to-stone-500',
    generationMode: 'environment',
    prompt: `Daytime outdoor scene with clear blue sky. A large white Mercedes truck - the Dubai Police Mobile Forensic Unit - parked on light gray/beige paved ground. The truck has a green horizontal stripe along its side, with "DUBAI POLICE" text and "MOBILE FORENSIC UNIT" written in green. The Dubai Police green circular logo is visible on the side. Multiple white satellite communication dishes mounted on the roof of the vehicle. Several black hard-shell equipment cases (Pelican-style) arranged on the ground in front of the truck. The Dubai city skyline is faintly visible in the background on the right side. Bright daylight with slight atmospheric haze. Professional field operations deployment scene.`
  },
  BALLISTICS_LAB: {
    id: 'ballistics_lab',
    name: 'Ballistics Range',
    description: 'Industrial testing facility with soundproofing and targets.',
    tags: ['Scene Swap', 'Industrial'],
    referenceImage: CLOUDFLARE_STYLE_URLS.ballistics_lab,
    previewImage: '/Style Images/Environment/ballistics-range.jpg',
    gradient: 'from-orange-600 to-red-600',
    generationMode: 'environment',
    prompt: `Indoor ballistics testing range with distinctive acoustic treatment. Dark gray pyramid-shaped acoustic foam panels covering both the ceiling and upper walls, creating a sound-dampening environment. The walls below the acoustic panels are painted green. Long narrow corridor perspective leading to an illuminated target area at the far end. Overhead fluorescent light strips running along the ceiling providing industrial lighting. Warm orange accent lights visible on the ceiling near the entrance. A stainless steel counter/examination table in the foreground with some ballistics equipment. Dubai Police logo/signage visible on the right wall. Industrial, functional, professional weapons testing and forensic ballistics facility atmosphere.`
  }
};

// ----------------------------------------------------------------------
// MODE 2: TRANSFORMATION STYLES (Outfit + Background Changes)
// ----------------------------------------------------------------------
export const TRANSFORMATION_STYLES = {
  FORENSIC_SCIENTIST: {
    id: 'forensic_scientist',
    name: 'Forensic Scientist',
    description: 'Lab coat, gloves, holding evidence in a sterile lab.',
    tags: ['Role', 'Lab'],
    referenceImage: CLOUDFLARE_STYLE_URLS.forensic_scientist,
    previewImage: '/Style Images/Full Transformation/forensic-scientist.png',
    gradient: 'from-green-600 to-teal-600',
    generationMode: 'transformation',
    prompt: `Create a PHOTOREALISTIC action photograph transforming the person into a Dubai Police Forensic Scientist actively working in the lab.

CRITICAL: This must look like a real photograph, NOT a painting, illustration, or digital art. Use realistic skin texture, natural lighting, and genuine photographic quality.

PRESERVE IDENTITY: Keep the person's exact face, facial features, skin tone, age, facial hair, and natural appearance. If the person's body is visible in the source image, preserve their body shape and build. This is the most critical requirement.

OUTFIT (NO logos, emblems, badges, or branding):
- Pristine white laboratory coat with front buttons
- Bright blue nitrile laboratory gloves on both hands

POSE & ACTION (ACTIVE WORK POSE):
- Three-quarter angle shot showing person from waist up
- Person is leaning forward slightly, looking DOWN at a microscope
- Hands positioned on microscope controls, actively adjusting focus
- Concentrated expression, eyes focused downward on work
- Natural working posture, caught in the middle of analysis
- Glass test tube with emerald green liquid visible on lab bench nearby

ENVIRONMENT:
- Sterile modern laboratory with white walls
- Clean white lab bench with microscope as main equipment
- Additional glass bottles and lab supplies scattered on bench
- Blurred laboratory shelving in background
- Bright, even clinical LED lighting from overhead

PHOTOGRAPHY STYLE: Candid workplace photography, PHOTOREALISTIC, real camera photograph look, sharp focus on scientist, f/2.0 depth of field, natural skin pores and texture visible, professional documentary-style lab photography, NOT painted or illustrated, 2:3 vertical format.`
  },
  SWAT_OFFICER: {
    id: 'swat_officer',
    name: 'SWAT Officer',
    description: 'Full tactical gear, vest, and headset.',
    tags: ['Role', 'Tactical'],
    referenceImage: CLOUDFLARE_STYLE_URLS.swat_officer,
    previewImage: '/Style Images/Full Transformation/swat-officer.png',
    gradient: 'from-gray-700 to-black',
    generationMode: 'transformation',
    prompt: `Create a PHOTOREALISTIC action photograph transforming the person into a Dubai Police SWAT Tactical Officer on alert.

CRITICAL: This must look like a real photograph, NOT a painting, illustration, or digital art. Use realistic skin texture, natural lighting, and genuine photographic quality.

PRESERVE IDENTITY: Keep the person's exact face, facial features, skin tone, age, facial hair, and natural appearance fully visible. If the person's body is visible in the source image, preserve their body shape and build. Do NOT add helmet or face covering. This is the most critical requirement.

OUTFIT (NO logos, emblems, badges, or branding - only plain text allowed):
- Heavy black tactical ballistic vest with MOLLE webbing
- Large white "POLICE" text printed across the chest of vest
- Black tactical uniform underneath (long sleeves)
- Black tactical gloves

POSE & ACTION (TACTICAL ALERT POSE):
- Three-quarter body shot from waist up
- Person in tactical ready stance, body turned 45 degrees to the side
- One hand on tactical vest radio/equipment, other hand ready at side
- Head turned looking alertly to the side (not at camera)
- Intense, focused expression scanning the area
- Professional tactical operator posture, ready to move

ENVIRONMENT:
- Dark industrial/tactical training facility background
- Concrete walls with weathered texture
- Glowing emerald green neon "SAFE ZONE" text sign visible in background
- Moody, dramatic atmosphere with atmospheric haze
- Low-key cinematic lighting with green accent rim light

PHOTOGRAPHY STYLE: Tactical documentary photography, PHOTOREALISTIC, real camera photograph look, dramatic low-light cinematography, f/1.8 shallow depth of field, sharp focus on operator, natural skin texture visible, high contrast with green accent, NOT painted or illustrated, 2:3 vertical format.`
  },
  DETECTIVE: {
    id: 'detective',
    name: 'Senior Detective',
    description: 'Formal suit, badge, and investigation board background.',
    tags: ['Role', 'Investigation'],
    referenceImage: CLOUDFLARE_STYLE_URLS.senior_detective,
    previewImage: '/Style Images/Full Transformation/senior-detective.png',
    gradient: 'from-blue-600 to-gray-600',
    generationMode: 'transformation',
    prompt: `Create a PHOTOREALISTIC portrait photograph transforming the person into a Senior Dubai Police Detective.

CRITICAL: This must look like a real photograph, NOT a painting, illustration, or digital art. Use realistic skin texture, natural lighting, and genuine photographic quality.

PRESERVE IDENTITY: Keep the person's exact face, facial features, skin tone, age, facial hair, and natural appearance. If the person's body is visible in the source image, preserve their body shape and build. This is the most critical requirement.

OUTFIT (NO logos, emblems, badges, or branding):
- Crisp white dress shirt with collar, sleeves rolled up to forearms
- Black or dark charcoal dress pants
- Black leather belt

POSE & ACTION:
- Three-quarter body shot showing upper body and waist
- Person holding an open manila case file folder in one hand
- Other hand pointing at an investigation board
- Engaged, analytical expression
- Professional detective posture

ENVIRONMENT:
- Dark investigation room with cork board/evidence board in background
- Board has crime scene photos, documents, and red string connecting evidence
- Warm amber desk lamp providing key lighting from side
- Computer monitor with green screen visible in soft focus background
- Moody, film noir atmosphere

PHOTOGRAPHY STYLE: Cinematic detective portrait, PHOTOREALISTIC, real camera photograph look, dramatic side lighting, warm amber tones, f/1.8 shallow depth of field, sharp focus on subject, natural skin pores and texture visible, professional color grading, NOT painted or illustrated, 2:3 vertical format.`
  },
  CSI_FIELD_AGENT: {
    id: 'csi_field_agent',
    name: 'CSI Field Agent',
    description: 'Blue vest, flashlight, searching for clues at night.',
    tags: ['Role', 'Action'],
    referenceImage: CLOUDFLARE_STYLE_URLS.csi_field_agent,
    previewImage: '/Style Images/Full Transformation/csi-field-agent.png',
    gradient: 'from-amber-600 to-orange-500',
    generationMode: 'transformation',
    prompt: `Create a PHOTOREALISTIC action photograph transforming the person into a Dubai Police CSI Field Investigator.

CRITICAL: This must look like a real photograph, NOT a painting, illustration, or digital art. Use realistic skin texture, natural lighting, and genuine photographic quality.

PRESERVE IDENTITY: Keep the person's exact face, facial features, skin tone, age, facial hair, and natural appearance. If the person's body is visible in the source image, preserve their body shape and build. This is the most critical requirement.

OUTFIT (NO logos, emblems, badges, or branding - only plain text allowed):
- Bright royal blue tactical vest with large white text "CSI FORENSIC TEAM" on back
- High-visibility silver reflective stripes across vest
- Dark navy tactical pants
- Black tactical boots

POSE & ACTION:
- Person kneeling on one knee on asphalt ground
- Holding a purple/blue UV forensic light in hand
- UV light illuminating a white chalk evidence outline on the pavement
- Focused, concentrated expression examining evidence
- Dynamic field work posture

ENVIRONMENT:
- Nighttime outdoor crime scene on dark asphalt street
- Dubai city skyline with illuminated skyscrapers in distant background
- Police vehicle lights creating red and blue ambient glow in far background
- Dark moody atmosphere of active investigation
- Purple/violet UV light glow on the ground where evidence is being examined

PHOTOGRAPHY STYLE: Cinematic night photography, PHOTOREALISTIC, real camera photograph look, dramatic low-light scene, strong purple UV accent light, shallow depth of field (f/1.4), sharp focus on investigator, natural skin pores and texture visible, professional crime scene documentation style, NOT painted or illustrated, 2:3 vertical format.`
  },
  CYBER_SPECIALIST: {
    id: 'cyber_specialist',
    name: 'Cyber Specialist',
    description: 'Dark navy polo, analyzing screens in operations center.',
    tags: ['Role', 'Tech'],
    referenceImage: CLOUDFLARE_STYLE_URLS.cyber_specialist,
    previewImage: '/Style Images/Full Transformation/cyber-specialist.png',
    gradient: 'from-green-600 to-blue-600',
    generationMode: 'transformation',
    prompt: `Create a PHOTOREALISTIC portrait photograph transforming the person into a Dubai Police Cyber Security Specialist.

CRITICAL: This must look like a real photograph, NOT a painting, illustration, or digital art. Use realistic skin texture, natural lighting, and genuine photographic quality.

PRESERVE IDENTITY: Keep the person's exact face, facial features, skin tone, age, facial hair, and natural appearance. If the person's body is visible in the source image, preserve their body shape and build. This is the most critical requirement.

OUTFIT (NO logos, emblems, badges, or branding):
- Dark navy blue tactical polo shirt
- Professional, technical appearance
- ID badge on lanyard (optional detail)

POSE & ACTION:
- Side profile shot, person seated at cyber security workstation
- Pointing with one finger at data on computer monitors
- Analyzing information with focused, concentrated expression
- Professional analyst posture

ENVIRONMENT:
- Dark Security Operations Center (SOC) room
- Multiple computer monitors displaying bright emerald green code, network graphs, and data visualizations
- Screens show terminal windows, network diagrams, security dashboards
- Keyboard and mouse on desk
- High-tech cyber command center atmosphere
- Key lighting from monitor screens creating green glow on face

PHOTOGRAPHY STYLE: Cinematic tech portrait, PHOTOREALISTIC, real camera photograph look, dramatic emerald green monitor glow lighting, dark shadows, f/2.0 depth of field with monitors in focus, sharp detail on subject, natural skin pores and texture visible, cyberpunk color grading, NOT painted or illustrated, 2:3 vertical format.`
  },
  K9_HANDLER: {
    id: 'k9_handler',
    name: 'K9 Handler',
    description: 'Tactical olive uniform with a German Shepherd dog.',
    tags: ['Role', 'K9'],
    referenceImage: CLOUDFLARE_STYLE_URLS.k9_handler,
    previewImage: '/Style Images/Full Transformation/k9-handler.png',
    gradient: 'from-amber-500 to-yellow-500',
    generationMode: 'transformation',
    prompt: `Create a PHOTOREALISTIC portrait photograph transforming the person into a Dubai Police K9 Unit Handler.

CRITICAL: This must look like a real photograph, NOT a painting, illustration, or digital art. Use realistic skin texture, natural lighting, and genuine photographic quality.

PRESERVE IDENTITY: Keep the person's exact face, facial features, skin tone, age, facial hair, and natural appearance. If the person's body is visible in the source image, preserve their body shape and build. This is the most critical requirement.

OUTFIT (NO logos, emblems, badges, or branding):
- Olive green/tan tactical uniform (matching top and cargo pants)
- Tactical vest with pouches and equipment
- Tactical belt with radio and equipment
- Tactical boots

POSE & ACTION:
- Full body shot showing person from head to mid-thigh
- Standing in confident, authoritative stance
- One arm extended forward pointing/commanding
- Professional K9 handler posture
- Alert German Shepherd police dog sitting obediently beside the person

ENVIRONMENT:
- Outdoor K9 training facility
- Green grass field
- Colorful dog agility training equipment visible in background (tunnels, jumps)
- Clear blue sky
- Golden hour sunlight creating warm backlighting
- Soft atmospheric haze from sunset
- Professional outdoor training atmosphere

PHOTOGRAPHY STYLE: Golden hour outdoor portrait, PHOTOREALISTIC, real camera photograph look, warm sunset backlighting, soft atmospheric glow, f/2.8 depth of field with slight background blur, sharp focus on handler and dog, natural skin pores and texture visible, warm professional color grading, NOT painted or illustrated, 2:3 vertical format.`
  },
  DRONE_PILOT: {
    id: 'drone_pilot',
    name: 'Drone Pilot',
    description: 'Operating surveillance drone with controller and monitors.',
    tags: ['Role', 'Tech'],
    referenceImage: CLOUDFLARE_STYLE_URLS.drone_pilot,
    previewImage: '/Style Images/Full Transformation/drone-pilot.png',
    gradient: 'from-sky-500 to-blue-600',
    generationMode: 'transformation',
    prompt: `Create a PHOTOREALISTIC portrait photograph transforming the person into a Drone Pilot operating surveillance equipment.

CRITICAL: This must look like a real photograph, NOT a painting, illustration, or digital art. Use realistic skin texture, natural lighting, and genuine photographic quality.

PRESERVE IDENTITY: Keep the person's exact face, facial features, skin tone, age, facial hair, and natural appearance. If the person's body is visible in the source image, preserve their body shape and build. Do NOT change the person's ethnicity, facial hair style, or grooming. This is the most critical requirement.

OUTFIT (NO logos, emblems, badges, or branding):
- Dark navy blue tactical flight suit/uniform
- Black tactical vest with radio and equipment pouches
- Aviation-style sunglasses pushed up on forehead
- Black tactical gloves

POSE & ACTION:
- Three-quarter body shot from waist up
- Person holding a professional drone controller with both hands
- Looking up at the sky, tracking a drone
- Focused, professional expression
- Confident operator stance

ENVIRONMENT:
- Outdoor helipad or rooftop location in Dubai
- Modern white quadcopter drone hovering in the mid-background
- Dubai skyline with Burj Khalifa visible in the distance
- Clear blue sky with wispy clouds
- Portable ground control station with monitors showing aerial footage nearby
- Morning golden light

PHOTOGRAPHY STYLE: Professional outdoor portrait, PHOTOREALISTIC, real camera photograph look, bright daylight with soft shadows, f/2.8 depth of field, sharp focus on operator, natural skin texture visible, clean professional color grading, NOT painted or illustrated, 2:3 vertical format.`
  },
  ARSON_INVESTIGATOR: {
    id: 'arson_investigator',
    name: 'Arson Investigator',
    description: 'Fire-resistant gear examining a burned crime scene.',
    tags: ['Role', 'Investigation'],
    referenceImage: CLOUDFLARE_STYLE_URLS.arson_investigator,
    previewImage: '/Style Images/Full Transformation/arson-investigator.png',
    gradient: 'from-orange-500 to-red-600',
    generationMode: 'transformation',
    prompt: `Create a PHOTOREALISTIC action photograph transforming the person into an Arson Investigator examining a fire scene.

CRITICAL: This must look like a real photograph, NOT a painting, illustration, or digital art. Use realistic skin texture, natural lighting, and genuine photographic quality.

PRESERVE IDENTITY: Keep the person's exact face, facial features, skin tone, age, facial hair, and natural appearance. If the person's body is visible in the source image, preserve their body shape and build. Do NOT change the person's ethnicity, facial hair style, or grooming. This is the most critical requirement.

EXPRESSION: Analytical, investigative expression while examining evidence.

OUTFIT (NO logos, emblems, badges, or branding):
- Tan/khaki fire-resistant coveralls
- Yellow reflective safety strips on chest and arms
- Black heavy-duty work boots
- White N95 respirator mask pulled down around neck
- Safety glasses pushed up on head
- Black work gloves

POSE & ACTION:
- Three-quarter body shot from knees up
- Crouching or kneeling examining burned debris
- Holding a flashlight in one hand, pointing at evidence
- Other hand holding a small evidence collection tool
- Focused, analytical expression studying burn patterns

ENVIRONMENT:
- Interior of a burned-out building or room
- Charred walls and ceiling with exposed beams
- Ash and debris on the floor
- Smoke damage and soot patterns visible on walls
- Shafts of light coming through damaged roof/windows
- Yellow evidence markers placed near burn origin point
- Dark, moody atmosphere with dramatic contrast

PHOTOGRAPHY STYLE: Documentary investigation photography, PHOTOREALISTIC, real camera photograph look, dramatic chiaroscuro lighting from window light, f/2.0 shallow depth of field, sharp focus on investigator, natural skin texture visible, desaturated color palette with warm amber highlights, NOT painted or illustrated, 2:3 vertical format.`
  },
  BOMB_DISPOSAL_TECHNICIAN: {
    id: 'bomb_disposal_technician',
    name: 'Bomb Disposal Technician',
    description: 'Heavy EOD protective suit in a tense urban setting.',
    tags: ['Role', 'Tactical'],
    referenceImage: CLOUDFLARE_STYLE_URLS.bomb_disposal_technician,
    previewImage: '/Style Images/Full Transformation/bomb-disposal-technician.png',
    gradient: 'from-stone-500 to-zinc-700',
    generationMode: 'transformation',
    prompt: `Create a PHOTOREALISTIC portrait photograph transforming the person into a Bomb Disposal Technician in protective gear.

CRITICAL: This must look like a real photograph, NOT a painting, illustration, or digital art. Use realistic skin texture, natural lighting, and genuine photographic quality.

PRESERVE IDENTITY: Keep the person's exact face, facial features, skin tone, age, facial hair, and natural appearance. If the person's body is visible in the source image, preserve their body shape and build. Do NOT change the person's ethnicity, facial hair style, or grooming. Face must be fully visible - NO helmet or face shield. This is the most critical requirement.

EXPRESSION: Intense, focused expression showing calm professionalism under pressure.

OUTFIT (NO logos, emblems, badges, or branding):
- Heavy olive green/khaki EOD bomb disposal suit (upper body portion visible)
- Thick padded protective collar around neck
- Suit appears heavy and armored with protective plating
- Black tactical gloves
- Helmet held under arm or placed nearby (NOT on head)

POSE & ACTION:
- Three-quarter body shot from waist up
- Standing in a composed, ready stance
- One hand adjusting equipment on the suit
- Calm but intense expression showing focus and bravery
- Professional demeanor under pressure

ENVIRONMENT:
- Urban outdoor setting, empty street or parking area
- Bomb disposal robot visible in the background
- Police cordon tape in the far background
- Emergency vehicles with lights visible at distance
- Dusty, tense atmosphere
- Harsh midday sunlight creating dramatic shadows

PHOTOGRAPHY STYLE: Dramatic documentary photography, PHOTOREALISTIC, real camera photograph look, harsh natural lighting, high contrast, f/2.8 depth of field, sharp focus on technician, natural skin texture visible, gritty realistic color grading, NOT painted or illustrated, 2:3 vertical format.`
  }
};

// Legacy STYLES object for backwards compatibility - defaults to environment styles
export const STYLES = ENVIRONMENT_STYLES;

// Timing constants
export const COUNTDOWN_DURATION = 7; // seconds
export const AUTO_RESET_TIMEOUT = 60000; // 60 seconds

// Loading messages for generating images
export const GENERATING_MESSAGES = [
  'Analyzing image composition...',
  'Processing transformation...',
  'Applying forensic styling...',
  'Enhancing image quality...',
  'Finalizing your photo...'
];

// Loading messages for saving images
export const SAVING_MESSAGES = [
  'Preparing your image...',
  'Uploading to cloud storage...',
  'Generating download link...',
  'Creating QR code...',
  'Almost ready!'
];

// Legacy export for backwards compatibility
export const LOADING_MESSAGES = GENERATING_MESSAGES;

// Firebase collection path
export const IMAGES_COLLECTION = 'generated_images';

// TTL duration (24 hours in milliseconds)
export const IMAGE_TTL = 24 * 60 * 60 * 1000;